// Server state (TanStack Query). Each mutation invalidates the queries whose
// data it changes, so screens refresh themselves after a save.
import type {
  AuditEntry,
  AuditMonth,
  Client,
  ClientDocument,
  ClientInput,
  ClientUpdateInput,
  Company,
  CompanyRegistrationInput,
  CompanySummary,
  CompanyUpdateInput,
  CompanyWorkspace,
  DocumentExtraction,
  DocumentType,
  Invitation,
  InvitationInput,
  IsoMonth,
  Me,
  Member,
  Operation,
  OperationInput,
  OperationUpdateInput,
  PublicInvitation,
  Team,
} from "@pcj/shared";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, api } from "./api";

export const queryKeys = {
  me: ["me"] as const,
  workspace: (companyId: string) => ["workspace", companyId] as const,
  team: (companyId: string) => ["team", companyId] as const,
  /** Prefix of every audit log query of a company (invalidating it refreshes them all). */
  auditLog: (companyId: string) => ["audit-log", companyId] as const,
  auditMonths: (companyId: string) => ["audit-log", companyId, "months"] as const,
  auditLogMonth: (companyId: string, month: IsoMonth) => ["audit-log", companyId, "month", month] as const,
  chamberCompanies: ["chamber-companies"] as const,
  invitation: (token: string) => ["invitation", token] as const,
};

/** Entries per page of a month of the audit log. */
export const AUDIT_PAGE_SIZE = 200;

/** The signed-in user, or `null` when there is no session. */
export function useMe() {
  return useQuery({
    queryKey: queryKeys.me,
    queryFn: async () => {
      try {
        return await api.get<Me>("/me");
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) return null;
        throw error;
      }
    },
    staleTime: 60_000,
  });
}

export function useRegisterCompany() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CompanyRegistrationInput) => api.post<Company>("/onboarding/company", input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.me }),
  });
}

export function useWorkspace(companyId: string) {
  return useQuery({
    queryKey: queryKeys.workspace(companyId),
    queryFn: () => api.get<CompanyWorkspace>(`/companies/${companyId}/workspace`),
  });
}

export function useChamberCompanies() {
  return useQuery({
    queryKey: queryKeys.chamberCompanies,
    queryFn: () => api.get<CompanySummary[]>("/chamber/companies"),
  });
}

export function useTeam(companyId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.team(companyId),
    queryFn: () => api.get<Team>(`/companies/${companyId}/team`),
    enabled,
  });
}

/** Months of the audit log with how many entries each holds, newest first. */
export function useAuditMonths(companyId: string) {
  return useQuery({
    queryKey: queryKeys.auditMonths(companyId),
    queryFn: () => api.get<AuditMonth[]>(`/companies/${companyId}/audit-log/months`),
  });
}

/** One month of the audit log, newest first, a page at a time. */
export function useAuditLogMonth(companyId: string, month: IsoMonth, enabled = true) {
  return useInfiniteQuery({
    queryKey: queryKeys.auditLogMonth(companyId, month),
    queryFn: ({ pageParam }) =>
      api.get<AuditEntry[]>(
        `/companies/${companyId}/audit-log?month=${month}&limit=${AUDIT_PAGE_SIZE}${pageParam ? `&before=${pageParam}` : ""}`,
      ),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => (lastPage.length === AUDIT_PAGE_SIZE ? lastPage.at(-1)?.id : undefined),
    enabled,
  });
}

export function usePublicInvitation(token: string) {
  return useQuery({
    queryKey: queryKeys.invitation(token),
    queryFn: () => api.get<PublicInvitation>(`/invitations/${token}`),
    retry: false,
  });
}

/** Every write of the company dashboard; each one refreshes the workspace and the audit log. */
export function useCompanyActions(companyId: string) {
  const queryClient = useQueryClient();
  const base = `/companies/${companyId}`;
  const onSuccess = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.workspace(companyId) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.auditLog(companyId) }),
    ]);

  return {
    updateCompany: useMutation({
      mutationFn: (input: CompanyUpdateInput) => api.patch<Company>(base, input),
      onSuccess,
    }),
    createClient: useMutation({
      mutationFn: (input: ClientInput) => api.post<Client>(`${base}/clients`, input),
      onSuccess,
    }),
    updateClient: useMutation({
      mutationFn: ({ id, ...input }: ClientUpdateInput & { id: string }) =>
        api.patch<Client>(`${base}/clients/${id}`, input),
      onSuccess,
    }),
    deleteClient: useMutation({
      mutationFn: (id: string) => api.delete(`${base}/clients/${id}`),
      onSuccess,
    }),
    createOperation: useMutation({
      mutationFn: (input: OperationInput) => api.post<Operation>(`${base}/operations`, input),
      onSuccess,
    }),
    updateOperation: useMutation({
      mutationFn: ({ id, ...input }: OperationUpdateInput & { id: string }) =>
        api.patch(`${base}/operations/${id}`, input),
      onSuccess,
    }),
    deleteOperation: useMutation({
      mutationFn: (id: string) => api.delete(`${base}/operations/${id}`),
      onSuccess,
    }),
    dismissAlert: useMutation({
      mutationFn: (input: { alertId: string; reason?: string }) => api.post(`${base}/dismissed-alerts`, input),
      onSuccess,
    }),
    restoreAlert: useMutation({
      mutationFn: (alertId: string) => api.delete(`${base}/dismissed-alerts/${alertId}`),
      onSuccess,
    }),
    fileZeroReport: useMutation({
      mutationFn: ({ month, folio }: { month: string; folio?: string }) =>
        api.put(`${base}/zero-reports/${month}`, { folio }),
      onSuccess,
    }),
    unfileZeroReport: useMutation({
      mutationFn: (month: string) => api.delete(`${base}/zero-reports/${month}`),
      onSuccess,
    }),
    uploadDocument: useMutation({
      mutationFn: ({
        clientId,
        file,
        type,
        note,
      }: {
        clientId: string;
        file: File;
        type: DocumentType;
        note?: string;
      }) => {
        const form = new FormData();
        form.set("file", file);
        form.set("type", type);
        if (note) form.set("note", note);
        return api.postForm<ClientDocument>(`${base}/clients/${clientId}/documents`, form);
      },
      onSuccess,
    }),
    deleteDocument: useMutation({
      mutationFn: (documentId: string) => api.delete(`${base}/documents/${documentId}`),
      onSuccess,
    }),
    /** Reads client data from a digital PDF; nothing is stored, so nothing is refreshed. */
    extractDocument: useMutation({
      mutationFn: (file: File) => {
        const form = new FormData();
        form.set("file", file);
        return api.postForm<DocumentExtraction>(`${base}/document-extractions`, form);
      },
    }),
  };
}

/** URL of a document's content: shown in the portal, or downloaded with `download`. */
export const documentUrl = (companyId: string, documentId: string, download = false) =>
  `/api/v1/companies/${companyId}/documents/${documentId}/content${download ? "?download=1" : ""}`;

/** URL of the operations summary PDF: one month, or every month. */
export const operationsReportUrl = (companyId: string, month?: IsoMonth) =>
  `/api/v1/companies/${companyId}/reports/operations${month ? `?month=${month}` : ""}`;

export function useTeamActions(companyId: string) {
  const queryClient = useQueryClient();
  const onSuccess = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.team(companyId) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.auditLog(companyId) }),
    ]);
  return {
    invite: useMutation({
      mutationFn: (input: InvitationInput) =>
        api.post<{ invitation: Invitation; link: string; emailSent: boolean }>(
          `/companies/${companyId}/invitations`,
          input,
        ),
      onSuccess,
    }),
    revoke: useMutation({
      mutationFn: (invitationId: string) => api.delete(`/companies/${companyId}/invitations/${invitationId}`),
      onSuccess,
    }),
    /** Turns a member's access off (`active: false`) or back on. */
    setActive: useMutation({
      mutationFn: ({ userId, active }: { userId: string; active: boolean }) =>
        api.post<Member>(`/companies/${companyId}/members/${userId}/${active ? "reactivate" : "deactivate"}`),
      onSuccess,
    }),
  };
}
